import { gql } from '@apollo/client';
import styled from 'styled-components';
import { metricsQueryString } from '../../utils/utils';
import ResourcesTable from './ResourcesTable';
import { timeColumns } from '../../utils/columns';
import { getFirstQueryResult } from '../../utils/graphql';
import { asCheck, asTruncatedText } from '../../utils/formatters';
import IconContainer from '../basics/IconContainer';
import FilledCircleIcon from '../icons/FilledCircleIcon';
import { UncontrolledWhiteTooltip } from '../basics/WhiteTooltip';

const StyledIconContainer = styled(IconContainer)`
  svg {
    color: var(--overyield-orange);
  }
`;

const formatCropName = (name, crop) => {
  const missingFields = [
    crop.unitCost ? null : 'unit cost',
    crop.yield ? null : 'yield',
  ].filter((v) => v);
  return (
    <div className="d-flex align-items-center">
      {asTruncatedText(name)}
      {!!missingFields.length && (
        <>
          <StyledIconContainer
            id={`message-dot-${crop.id}`}
            className="ml-2"
            iconSize="10px"
          >
            <FilledCircleIcon />
          </StyledIconContainer>
          <UncontrolledWhiteTooltip
            target={`message-dot-${crop.id}`}
            placement="right"
            popperClassName="font-small"
          >
            No {missingFields.join(' or ')} data configured
          </UncontrolledWhiteTooltip>
        </>
      )}
    </div>
  );
};

const COLS = (isTemplate) => [
  {
    accessor: 'name',
    displayString: 'Name',
    format: formatCropName,
  },
  ...(isTemplate
    ? [
        {
          accessor: 'growingZones',
          displayString: 'Growing zones',
          format: (v) => asTruncatedText(v.join(',')),
        },
        {
          accessor: 'isPublished',
          displayString: 'Published',
          format: asCheck,
          isSmall: true,
        },
      ]
    : [
        {
          accessor: 'inUse',
          displayString: 'In Use',
          format: asCheck,
          style: { verticalAlign: 'middle' },
          isSmall: true,
        },
      ]),
  ...timeColumns,
];

const CROP_COLS = COLS(false);
const TEMPLATE_COLS = COLS(true);
const TEAM_TEMPLATE_COLS = TEMPLATE_COLS.filter(
  ({ accessor }) => accessor !== 'isPublished',
);

export const CropTemplatesQuery = gql`
  query CropTemplatesQuery {
    cropTemplates {
      id
      ${metricsQueryString(TEMPLATE_COLS)}
      yield {
        id
      }
      unitCost
    }
  }
`;

const TeamCropTemplatesQuery = gql`
  query TeamCropTemplatesQuery($id: ID!) {
    team(id: $id) {
      id
      cropTemplates {
        id
        ${metricsQueryString(TEAM_TEMPLATE_COLS)}
        yield {
          id
        }
        unitCost
      }
    }
  }
`;

export const RawCropsQuery = gql`
  query RawCropsQuery($farmId: ID!) {
    rawCrops(farmId: $farmId) {
      id
      ${metricsQueryString(CROP_COLS)}
      yield {
        id
      }
      unitCost
    }
  }
`;

export const FarmCropsTable = () => (
  <ResourcesTable
    query={RawCropsQuery}
    cols={CROP_COLS}
    newText="New Crop"
    title="Crops"
    sortColumn={['inUse', 'name']}
    sortDirection={['desc', 'asc']}
  />
);

export const TemplateCropsTable = () => (
  <ResourcesTable
    query={CropTemplatesQuery}
    cols={TEMPLATE_COLS}
    newText="New Template"
    filter={(filterValue) => ({ name, growingZones }) =>
      name.toLowerCase().includes(filterValue) ||
      growingZones.includes(filterValue)}
    searchPlaceholder="Search by name or zone"
    title="Crop Templates"
  />
);

export const TeamCropTemplatesTable = ({ teamId }) => (
  <ResourcesTable
    query={TeamCropTemplatesQuery}
    queryOptions={{ variables: { id: teamId } }}
    getQueryResult={(data) => getFirstQueryResult(data)?.cropTemplates}
    cols={TEAM_TEMPLATE_COLS}
    newText="New Template"
    filter={(filterValue) => ({ name, growingZones }) =>
      name.toLowerCase().includes(filterValue) ||
      growingZones.includes(filterValue)}
    searchPlaceholder="Search by name or zone"
    title="Crop Templates"
  />
);
